import moment from "moment";
import {
  capitalize,
  isEmpty,
  isEqual,
  isString,
  last,
  orderBy,
  sumBy,
} from "lodash";

import PageConfig from "../config";
import DashboardWidgetService from "../../../../services/dashboard-widgets";
// import AppConfig from "../../../../config/";

// TODO: Test Data: remove when completed
// import SampleProdData from "../sample-prod-data.json";

const generateMonthlySystemStatStorageKey = (month) => {
  const { STORAGE_PREFIX, SYSTEM_STAT_PREFIX } = PageConfig;
  return `${STORAGE_PREFIX}-${SYSTEM_STAT_PREFIX}-${month}`;
};

const checkStorageConfigChanged = () => {
  const { STORAGE_KEY_MAP } = PageConfig;
  let output = false;
  for (const [configKey, storageKey] of Object.entries(STORAGE_KEY_MAP)) {
    const storedValue = localStorage.getItem(storageKey);
    if (storedValue == null) {
      output = true;
      break;
    }
    const configValue = PageConfig[configKey];
    if (!isEqual(storedValue, configValue.toString())) {
      output = true;
      break;
    }
  }
  return output;
};

const getLegendNameBySaleType = ({ saleType }) => {
  const { AD_SALES_TYPE } = PageConfig;
  if (saleType === AD_SALES_TYPE.ALL) return "new + renewals";
  else if (saleType === AD_SALES_TYPE.NEW) return "new";
  else if (saleType === AD_SALES_TYPE.RENEW) return "renew";
  else return "invalid sale type";
};

const getDailySaleSummeryKeyBySaleType = ({ saleType }) => {
  const { AD_SALES_TYPE } = PageConfig;
  if (saleType === AD_SALES_TYPE.RENEW) return "renew";
  else if (saleType === AD_SALES_TYPE.NEW) return "new";
  else return "total";
};

const getCurrentSalesMonthEndDate = ({ format = undefined } = {}) => {
  const { MONTHLY_SALES_REFRESH_CALENDAR_DATE } = PageConfig;
  const currentDate = moment();
  let salesMonth;
  if (currentDate.date() >= MONTHLY_SALES_REFRESH_CALENDAR_DATE) {
    salesMonth = currentDate.add(1, "month");
  } else {
    salesMonth = currentDate;
  }
  const targetEndDate = salesMonth.set(
    "date",
    MONTHLY_SALES_REFRESH_CALENDAR_DATE - 1
  );
  if (format && isString(format)) {
    return salesMonth.format(format).toLowerCase();
  }
  return targetEndDate;
};

const getCurrentSalesMonthStartDate = ({ format = undefined } = {}) => {
  const { MONTHLY_SALES_REFRESH_CALENDAR_DATE } = PageConfig;
  let output = moment();
  if (output.get("date") < MONTHLY_SALES_REFRESH_CALENDAR_DATE) {
    output = output.subtract(1, "month");
  }
  output = output.set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE);
  if (!isEmpty(format) && isString(format)) {
    return output.format(format).toLocaleLowerCase();
  }
  return output;
};

const orderDailySalesByCalendarMonth = ({ salesMap }) => {
  const output = {};
  const orderedMonths = moment.months().map((item) => item.toLowerCase());
  orderedMonths.forEach((item) => {
    if (salesMap[item]) {
      output[item] = salesMap[item];
    }
  });
  return output;
};

const persistConfigValuesToStorage = () => {
  const { STORAGE_KEY_MAP } = PageConfig;
  for (const [configKey, storageKey] of Object.entries(STORAGE_KEY_MAP)) {
    const configValue = PageConfig[configKey];
    localStorage.setItem(storageKey, configValue);
  }
};

const flushStorageValues = () => {
  const { STORAGE_PREFIX } = PageConfig;
  const storageKeys = Object.keys(localStorage);
  for (const tempKey of storageKeys) {
    const shouldDelete = tempKey.includes(STORAGE_PREFIX);
    if (shouldDelete) {
      localStorage.removeItem(tempKey);
    }
  }
};

const persistLastSalesMonthSystemStats = async () => {
  const {
    SALES_TARGET_STARTED_DATE,
    DATE_FORMAT,
    FULL_MONTH_NAME_FORMAT,
    MONTHLY_SALES_REFRESH_CALENDAR_DATE,
  } = PageConfig;
  try {
    const currentDate = moment();
    const targetStartedDate = moment(SALES_TARGET_STARTED_DATE);
    const monthDiff = Math.ceil(
      currentDate.diff(targetStartedDate, "month", true)
    );
    for (let temp = monthDiff; temp > 0; temp--) {
      const salesMonthStart = moment()
        .set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE)
        .subtract(temp, "month");
      const salesMonthEnd = moment()
        .set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE)
        .subtract(temp - 1, "month")
        .subtract(1, "day");
      const salesMonth = salesMonthEnd.format(FULL_MONTH_NAME_FORMAT);
      const storageKey = generateMonthlySystemStatStorageKey(
        salesMonth.toLowerCase()
      );

      const storedValue = localStorage.getItem(storageKey);
      if (storedValue == null) {
        const apiResponse = await DashboardWidgetService.getSystemStat({
          fromDate: salesMonthStart.format(DATE_FORMAT),
          toDate: salesMonthEnd.format(DATE_FORMAT),
        });
        if (!apiResponse.success) {
          // TODO: Error Handling
        }
        localStorage.setItem(storageKey, JSON.stringify(apiResponse.body));
      }
    }
  } catch (error) {
    console.error(">>>> persistLastSalesMonthSystemStats error ", error);
    // TODO: ERROR HANDLING
  }
};

// const persistSampleProdSystemStatsToDevEnv = () => {
//   for (const [storagKey, storageValue] of Object.entries(SampleProdData)) {
//     localStorage.setItem(storagKey, JSON.stringify(storageValue));
//   }
// };

const persistCurrentSalesMonthSystemStats = async () => {
  const {
    MONTHLY_SALES_REFRESH_CALENDAR_DATE,
    DATE_FORMAT,
    FULL_MONTH_NAME_FORMAT,
  } = PageConfig;
  const currentDate = moment();
  const saleMonthStartDate =
    currentDate.date() < MONTHLY_SALES_REFRESH_CALENDAR_DATE
      ? moment()
          .subtract(1, "month")
          .set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE)
      : moment().set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE);

  const apiResponse = await DashboardWidgetService.getSystemStat({
    fromDate: saleMonthStartDate.format(DATE_FORMAT),
    toDate: currentDate.format(DATE_FORMAT),
  });
  if (!apiResponse.success) {
    return; // TODO: Error Handling
  }
  const salesMonth = saleMonthStartDate
    .add(1, "month")
    .format(FULL_MONTH_NAME_FORMAT)
    .toLowerCase();
  const stoageKey = generateMonthlySystemStatStorageKey(salesMonth);
  localStorage.setItem(stoageKey, JSON.stringify(apiResponse.body));
};

const initializeDashboard = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const isConfigChanged = checkStorageConfigChanged();
      if (isConfigChanged) {
        flushStorageValues();
        persistConfigValuesToStorage();
      }
      /**
       * TODO: Remove when development is done
       */
      // const { TARGET_ENVIRONMENT, TARGET_ENVIRONMENTS } = AppConfig;
      // if (TARGET_ENVIRONMENT === TARGET_ENVIRONMENTS.DEVELOP) {
      //   persistSampleProdSystemStatsToDevEnv();
      // } else {
      //   persistLastSalesMonthSystemStats();
      //   persistCurrentSalesMonthSystemStats();
      // }
      await persistLastSalesMonthSystemStats();
      await persistCurrentSalesMonthSystemStats();

      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

const calculateDailySalesMap = () => {
  const output = {};
  const {
    SALES_TARGET_STARTED_DATE,
    FULL_MONTH_NAME_FORMAT,
    MONTHLY_SALES_REFRESH_CALENDAR_DATE,
  } = PageConfig;
  const analyticalStartDate = moment(SALES_TARGET_STARTED_DATE).subtract(
    1,
    "month"
  );
  const nowDate = moment();
  const monthsDiff = Math.ceil(
    nowDate.diff(analyticalStartDate, "month", true)
  );
  for (let temp = monthsDiff - 1; temp >= 0; temp--) {
    let currentMonth;
    if (temp > 0) {
      currentMonth = moment().subtract(temp - 1, "month");
    } else {
      currentMonth =
        nowDate.get("date") > MONTHLY_SALES_REFRESH_CALENDAR_DATE
          ? moment().add(1, "month")
          : moment();
    }
    const storageKey = generateMonthlySystemStatStorageKey(
      currentMonth.format(FULL_MONTH_NAME_FORMAT).toLowerCase()
    );
    const storedValue = localStorage.getItem(storageKey);
    if (storedValue === null) {
      continue;
    }
    const monthSales = [];
    const systemStats = JSON.parse(storedValue);
    if (!Array.isArray(systemStats)) continue;
    systemStats.forEach((item) => {
      const { date, dailyPublished, dailyNewPosts } = item;
      const summery = {};
      summery.date = date;
      summery.new = dailyNewPosts ?? 0;
      summery.renew = (dailyPublished ?? 0) - (dailyNewPosts ?? 0);
      summery.total = dailyPublished;
      monthSales.push(summery);
    });
    output[currentMonth.format(FULL_MONTH_NAME_FORMAT).toLowerCase()] = orderBy(
      monthSales,
      ["date"],
      "asc"
    );
  }
  const orderedOutput = orderDailySalesByCalendarMonth({ salesMap: output });
  return orderedOutput;
};

const generateTotalSalesChartDataset = ({ orderedSalesMap, chartType }) => {
  const {
    SALES_TARGET_STARTED_DATE,
    FULL_MONTH_NAME_FORMAT,
    AD_SALES_TYPE,
    LEGEND_DATE_FORMAT,
  } = PageConfig;
  const targetStartedBeforeMonth = moment(SALES_TARGET_STARTED_DATE)
    .subtract(1, "day")
    .format(FULL_MONTH_NAME_FORMAT)
    .toLowerCase();

  const labels = [];
  const datasetMap = {};

  const accumulators = {
    [getDailySaleSummeryKeyBySaleType({ saleType: AD_SALES_TYPE.NEW })]: 0,
    [getDailySaleSummeryKeyBySaleType({ saleType: AD_SALES_TYPE.RENEW })]: 0,
    [getDailySaleSummeryKeyBySaleType({ saleType: AD_SALES_TYPE.ALL })]: 0,
  };
  for (const currentType of Object.values(AD_SALES_TYPE)) {
    const temp = {};
    temp.label = getLegendNameBySaleType({ saleType: currentType });
    temp.data = [];
    datasetMap[getDailySaleSummeryKeyBySaleType({ saleType: currentType })] =
      temp;
  }
  for (const [monthName, sales] of Object.entries(orderedSalesMap)) {
    if (monthName === targetStartedBeforeMonth || !Array.isArray(sales)) {
      continue;
    }
    for (const item of sales) {
      labels.push(moment(item.date).format(LEGEND_DATE_FORMAT));
      for (const currentType of Object.values(AD_SALES_TYPE)) {
        const keyName = getDailySaleSummeryKeyBySaleType({
          saleType: currentType,
        });
        accumulators[keyName] += item[keyName];
        if (datasetMap[keyName] && Array.isArray(datasetMap[keyName].data)) {
          const tempArray = datasetMap[keyName].data;
          tempArray.push(accumulators[keyName]);
        }
      }
    }
  }
  if (chartType === "line") {
    return { labels, datasets: Object.values(datasetMap) };
  } else if (chartType === "pie") {
    const calcuationSaleTypes = [
      getDailySaleSummeryKeyBySaleType({ saleType: AD_SALES_TYPE.NEW }),
      getDailySaleSummeryKeyBySaleType({ saleType: AD_SALES_TYPE.RENEW }),
    ];
    const dataset = { data: [] };
    for (const currentType of calcuationSaleTypes) {
      const currentData = datasetMap[currentType]?.data ?? [];
      dataset.data.push(last(currentData));
    }
    return { labels: calcuationSaleTypes, datasets: [dataset] };
  }
};

const getMaxSalePreviouseMonth = ({ orderedSalesMap }) => {
  let maximumSalesMonth,
    maxValue = 0;
  Object.entries(orderedSalesMap).forEach(([storageKey, monthSales]) => {
    const tempTotal = sumBy(monthSales, "total");
    if (tempTotal >= maxValue) {
      maximumSalesMonth = storageKey;
      maxValue = tempTotal;
    }
  });
  return maximumSalesMonth;
};

const generateMonthlyTargetPerformanceLineChartDateset = ({
  orderedSalesMap,
  salesIncrement,
}) => {
  const {
    AD_SALES_TYPE,
    LEGEND_DATE_FORMAT,
    FULL_MONTH_NAME_FORMAT,
    MONTHLY_SALES_REFRESH_CALENDAR_DATE,
    AD_TARGET_RATIO,
  } = PageConfig;
  const labels = [];
  const datasetMap = {};
  const accumulators = {};
  const calcuationSaleTypes = [AD_SALES_TYPE.NEW, AD_SALES_TYPE.RENEW];
  calcuationSaleTypes.forEach((current) => {
    const keyName = getDailySaleSummeryKeyBySaleType({ saleType: current });
    datasetMap[keyName] = { label: keyName, data: [] };
  });
  const currentSaleMonth = getCurrentSalesMonthEndDate({
    format: FULL_MONTH_NAME_FORMAT,
  });
  const currentMonthSales = orderedSalesMap[currentSaleMonth];
  if (Array.isArray(currentMonthSales)) {
    currentMonthSales.forEach((current) => {
      const dateLabel = moment(current.date).format(LEGEND_DATE_FORMAT);
      labels.push(dateLabel);
      calcuationSaleTypes.forEach((curentType) => {
        const keyName = getDailySaleSummeryKeyBySaleType({
          saleType: curentType,
        });
        if (datasetMap[keyName] && Array.isArray(datasetMap[keyName].data)) {
          datasetMap[keyName]["data"].push(current[keyName]);
        }
      });
    });
    const previouseMonth = getMaxSalePreviouseMonth({ orderedSalesMap });
    const previouseMonthSales = orderedSalesMap[previouseMonth];

    if (Array.isArray(previouseMonthSales)) {
      const currenSalesMonthEndDate = getCurrentSalesMonthEndDate();
      const currentSaleMonthStartDate = getCurrentSalesMonthEndDate()
        .subtract(1, "month")
        .set("date", MONTHLY_SALES_REFRESH_CALENDAR_DATE);
      if (
        moment.isMoment(currenSalesMonthEndDate) &&
        moment.isMoment(currentSaleMonthStartDate)
      ) {
        accumulators.prevMonthTotalSales =
          sumBy(previouseMonthSales, (item) => item.total ?? 0) ?? 0;

        accumulators.nextMonthSalesTarget = Math.ceil(
          accumulators.prevMonthTotalSales * (1 + salesIncrement / 100)
        );
        const numOfDays = currenSalesMonthEndDate
          .add(1, "day")
          .diff(currentSaleMonthStartDate, "day");
        accumulators.numOfDays = numOfDays;
        accumulators.nextMonthNewAds =
          accumulators.nextMonthSalesTarget * AD_TARGET_RATIO.NEW;
        accumulators.nextMonthRenewAds =
          accumulators.nextMonthSalesTarget * AD_TARGET_RATIO.RENEW;
        accumulators.avgNextMonthNewAds = Math.ceil(
          accumulators.nextMonthNewAds / numOfDays
        );
        accumulators.avgNextMonthRenewAds = Math.ceil(
          accumulators.nextMonthRenewAds / numOfDays
        );
      }
      accumulators.currenSalesMonthEndDate = currenSalesMonthEndDate;
      accumulators.currentSaleMonthStartDate = currentSaleMonthStartDate;
      accumulators.nextMonthNewAds = Math.ceil(accumulators.nextMonthNewAds);
      accumulators.nextMonthRenewAds = Math.ceil(
        accumulators.nextMonthRenewAds
      );
      accumulators.currentNewAds = sumBy(currentMonthSales, (item) => item.new);
      accumulators.currentRenewAds = sumBy(
        currentMonthSales,
        (item) => item.renew
      );
      accumulators.currentTotalAds = sumBy(
        currentMonthSales,
        (item) => item.total
      );
      accumulators.currentDateIndex = moment().diff(
        currentSaleMonthStartDate,
        "day"
      );
      accumulators.currentAvgNewSales =
        accumulators.avgNextMonthNewAds * accumulators.currentDateIndex;
      accumulators.currentAvgRenewals =
        accumulators.avgNextMonthRenewAds * accumulators.currentDateIndex;

      for (const type of Object.values(AD_SALES_TYPE)) {
        if (!calcuationSaleTypes.includes(type)) continue;
        const legendName = getLegendNameBySaleType({ saleType: type });
        const datasetKey = `avg${capitalize(legendName)}`;
        const avgValue =
          type === AD_SALES_TYPE.NEW
            ? accumulators.avgNextMonthNewAds
            : accumulators.avgNextMonthRenewAds;
        const tempIterator = Array.from(labels ?? []).fill(avgValue);
        datasetMap[datasetKey] = {
          label: `${datasetKey} (${avgValue})`,
          data: tempIterator,
        };
      }
    }
  }
  return { labels, datasets: Object.values(datasetMap), accumulators };
};

const generateMonthlyTargetPerformancePieChartDateset = ({
  orderedSalesMap,
}) => {
  const { AD_SALES_TYPE, FULL_MONTH_NAME_FORMAT } = PageConfig;
  const data = [];
  const accumulators = {};
  const calcuationSaleTypes = [AD_SALES_TYPE.NEW, AD_SALES_TYPE.RENEW].map(
    (saleType) => getDailySaleSummeryKeyBySaleType({ saleType })
  );
  const currentSaleMonth = getCurrentSalesMonthEndDate();
  const monthSalesList =
    orderedSalesMap[
      currentSaleMonth.format(FULL_MONTH_NAME_FORMAT).toLowerCase()
    ];
  for (const label of calcuationSaleTypes) {
    const totalSales = sumBy(monthSalesList, (item) => item[label]);
    data.push(totalSales);
  }

  return {
    labels: calcuationSaleTypes,
    datasets: [{ data }],
    accumulators,
  };
};

const generateDailySalesTable = ({ orderedSalesMap }) => {
  const { FULL_MONTH_NAME_FORMAT, DATE_FORMAT } = PageConfig;
  const salesMonth = getCurrentSalesMonthEndDate({
    format: FULL_MONTH_NAME_FORMAT,
  });
  const currentSales = [...(orderedSalesMap[salesMonth] ?? [])];
  const salesMonthStart = getCurrentSalesMonthStartDate();
  const salesMonthEnd = getCurrentSalesMonthEndDate();
  const availableDates = currentSales.map((item) => item.date);
  let tempMoment = salesMonthStart;
  while (tempMoment.isSameOrBefore(salesMonthEnd)) {
    if (!availableDates.includes(tempMoment.format(DATE_FORMAT))) {
      currentSales.push({
        date: tempMoment.format(DATE_FORMAT),
        new: "-",
        renew: "-",
        total: "-",
      });
    }
    tempMoment.add(1, "days");
  }
  return currentSales;
};

const generateMonthlyTargetPerformanceChartDateset = ({
  orderedSalesMap,
  chartType,
  salesIncrement = 0.05,
}) => {
  switch (chartType) {
    case "pie":
      return generateMonthlyTargetPerformancePieChartDateset({
        orderedSalesMap,
      });
    case "line":
    default:
      return generateMonthlyTargetPerformanceLineChartDateset({
        orderedSalesMap,
        salesIncrement,
      });
  }
};

const PageDomain = {
  initializeDashboard,
  calculateDailySalesMap,
  generateTotalSalesChartDataset,
  getCurrentSalesMonthEndDate,
  generateMonthlyTargetPerformanceChartDateset,
  generateDailySalesTable,
};
export default PageDomain;
